import { useMemo } from "react";
import { showRequestErrorToast } from "../../services/ErrorService";
import { Loading } from "../loading/Loading";
import { useParams } from "react-router";
import { Particulars as ParticularsGrid } from "@idwal/idwal-react-components";
import { VesselInspectionResponse } from "../../types/responses/VesselInspectionResponse";
import dayjs from "dayjs";
import { formatNumber } from "../../utils/numberHelpers";

type Props = {
    data: VesselInspectionResponse | undefined;
    isLoading: boolean;
    error: unknown;
    isError: boolean;
};

export const Particulars = (props: Props) => {
    const { data, isLoading, error, isError } = props;

    const { inspectionId } = useParams();

    const includedKeys = [
        "ShipName",
        "IMONumber",
        "PreviousName",
        "PortOfRegistry",
        "ShipType",
        "Flag",
        "ClassificationSociety",
        "RegisteredOwner",
        "TechnicalManager",
        "Shipbuilder",
        "DateDelivered",
        "ShipAge",
        "CountryOfBuild",
        "KeelLaid",
        "Operator",
        "P&IClub",
        "Callsign",
        "Gearless",
        "Deadweight",
        "GrossTonnage",
        "NetTonnage",
        "Length",
        "Breadth",
        "Depth",
        "Draught",
        "Lightweight"
    ];

    const numberKeys = [
        "Deadweight",
        "GrossTonnage",
        "NetTonnage",
        "Length",
        "Breadth",
        "Depth",
        "Draught",
        "Lightweight"
    ];

    if (Number(data?.TEU)) {
        includedKeys.push("TEU");
    }

    const camelize = (str: string) => {
        return str
            .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: string, index) => {
                return index === 0 ? word.toLowerCase() : word.toUpperCase();
            })
            .replace(/\s+/g, "");
    };

    const particulars = useMemo(() => {
        const particularsData: Record<string, string> = {};

        if (data) {
            for (const key of includedKeys) {
                let value = data[key];

                if (value === undefined || value === null) {
                    value = "";
                }

                if (value === "Y") {
                    value = "Yes";
                }

                if (value === "N") {
                    value = "No";
                }

                if (numberKeys.includes(key)) {
                    value = formatNumber(value);
                }

                if (key === "ShipAge" && data.DateDelivered) {
                    const delivered = dayjs(
                        data.DateDelivered.split("/").reverse().join("-")
                    );
                    const now = dayjs();
                    const years = now.diff(delivered, "year");
                    const months = now.diff(delivered, "month") - years * 12;

                    value = `${years} years ${months} months`;
                }

                particularsData[camelize(key)] = value.toString();
            }
        }

        return particularsData;
    }, [data]);

    if (!inspectionId) {
        console.error("Particulars - No inspection id.");
        showRequestErrorToast("Particulars");
        return <div></div>;
    }

    if (isLoading || data === undefined) return <Loading />;

    if (isError) {
        console.error(error);
        showRequestErrorToast("Particulars");
        return <div></div>;
    }

    return <ParticularsGrid particulars={particulars} />;
};
